import { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import { useGetProfilePicture } from "hooks/query/useAccount";
import { getUserDetails, isTokenValid } from "utils/localStorageServices";

interface ProfilePictureProps {
  size?: number;
  initials?: string;
  className?: string;
  enabled?: boolean;
  src?: string | null;
  alt?: string;
  fallbackColor?: string;
}

export default function ProfilePicture({
  size = 75,
  initials,
  className = "",
  enabled = true,
  src,
  alt = "avatar",
  fallbackColor = "#F5693F",
}: ProfilePictureProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const hasValidAuth = !!getUserDetails() && isTokenValid();

  const { data: profilePicture, isLoading } = useGetProfilePicture(
    enabled && hasValidAuth && !src,
  );

  useEffect(() => {
    if (!profilePicture) {
      setImageUrl(null);
      return;
    }
    if (typeof profilePicture === "string") {
      setImageUrl(profilePicture);
    } else if (profilePicture.size > 0) {
      const url = URL.createObjectURL(profilePicture);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setImageUrl(null);
    }
  }, [profilePicture]);

  const loaderSize = size <= 30 ? 10 : size <= 80 ? 14 : 16;
  const initialsSize =
    size <= 44 ? "text-xs" : size <= 80 ? "text-xl" : "text-3xl";
  // Only fall back to the fetched image when this consumer opted in. The query
  // key is user-global, so with enabled=false useQuery still returns the
  // logged-in user's cached picture — guarding here keeps it from bleeding into
  // avatars that pass their own src (e.g. inbox participants).
  const candidateImageUrl = src || (enabled ? imageUrl : null);
  // A picture that fails to load (private blob container, deleted file, bad
  // content-type) is treated as absent so we fall through to the initials
  // instead of leaving a broken image in the avatar. Keying the failure on the
  // URL itself means a new picture is retried without any reset bookkeeping -
  // this component is reused across list rows.
  const resolvedImageUrl =
    candidateImageUrl && candidateImageUrl !== failedUrl
      ? candidateImageUrl
      : null;

  return (
    <div
      style={{ width: size, height: size }}
      className={`rounded-full flex items-center justify-center overflow-hidden ${className}`}
    >
      {isLoading && !src ? (
        <div className="w-full h-full bg-orange30 rounded-full flex items-center justify-center">
          <ClipLoader color="gray" size={loaderSize} />
        </div>
      ) : resolvedImageUrl ? (
        <img
          src={resolvedImageUrl}
          alt={alt}
          className="w-full h-full object-cover rounded-full"
          onError={() => setFailedUrl(resolvedImageUrl)}
        />
      ) : initials ? (
        <div
          className="w-full h-full rounded-full flex items-center justify-center"
          style={{ backgroundColor: fallbackColor }}
        >
          <span className={`text-white font-semibold ${initialsSize}`}>
            {initials}
          </span>
        </div>
      ) : (
        <div className="w-full h-full bg-orange30 rounded-full" />
      )}
    </div>
  );
}
